import { SuggestedFriend } from '@/types/friendship';
import { imageUrl } from '@/utils/image-url';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React, { useCallback } from 'react';
import { ActivityIndicator, Image, Pressable, Text, View } from 'react-native';

interface SuggestedFriendCardProps {
  suggestion: SuggestedFriend;
  onAddFriend: (userId: string) => void;
  isPending?: boolean;
}

export const SuggestedFriendCard = React.memo<SuggestedFriendCardProps>(({
  suggestion,
  onAddFriend,
  isPending,
}) => {
  const avatar = imageUrl(suggestion.image, 'thumbnail');
  const displayName = suggestion.name || suggestion.username || 'User';
  const normalizedUsername = (suggestion.displayUsername || suggestion.username || '').replace(/^@+/, '');

  const handlePress = useCallback(() => {
    const identifier = suggestion.username?.replace(/^@+/, '');
    if (identifier) {
      router.push({
        pathname: '/(root)/profile/[username]',
        params: { username: identifier },
      });
    }
  }, [suggestion.username]);

  const handleAddFriend = useCallback(() => {
    onAddFriend(suggestion.userId);
  }, [suggestion.userId, onAddFriend]);

  return (
    <Pressable
      className="mr-3 w-36"
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={`View ${displayName}'s profile`}
    >
      <View className="rounded-xl overflow-hidden bg-card/30 border border-border/50 p-3 items-center">
        {avatar ? (
          <Image
            source={{ uri: avatar }}
            className="w-16 h-16 rounded-full mb-2 bg-surface-secondary"
            accessibilityLabel={`${displayName}'s profile photo`}
          />
        ) : (
          <View className="w-16 h-16 rounded-full mb-2 items-center justify-center bg-surface-secondary">
            <Ionicons name="person" size={28} className="text-muted-foreground" />
          </View>
        )}

        <Text className="font-semibold text-sm text-foreground text-center" numberOfLines={1}>
          {displayName}
        </Text>

        {normalizedUsername ? (
          <Text className="text-xs text-muted-foreground text-center mb-1" numberOfLines={1}>
            @{normalizedUsername}
          </Text>
        ) : (
          <View className="mb-1" />
        )}

        {suggestion.mutualFriendCount > 0 && (
          <Text className="text-xs text-muted-foreground text-center mb-2" numberOfLines={1}>
            {suggestion.mutualFriendCount} mutual {suggestion.mutualFriendCount === 1 ? 'friend' : 'friends'}
          </Text>
        )}

        {suggestion.mutualFriendCount === 0 && <View className="mb-2" />}

        <Pressable
          onPress={handleAddFriend}
          disabled={isPending}
          className="bg-muted rounded-lg px-4 py-1.5 w-full items-center"
          accessibilityRole="button"
          accessibilityLabel={`Add ${displayName} as friend`}
        >
          {isPending ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <View className="flex-row items-center gap-1">
              <Ionicons name="add-outline" size={16} color="white" />
              <Text className="text-xs font-semibold text-foreground">Add Friend</Text>
            </View>
          )}
        </Pressable>
      </View>
    </Pressable>
  );
});

SuggestedFriendCard.displayName = 'SuggestedFriendCard';
